// src/pages/LoginPage.js (UPDATED WITH TOKEN HANDLING)
import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { loginUser } from "../api/api";
import { AuthContext } from "../context/AuthContext";
import { 
  FaEnvelope, 
  FaLock, 
  FaSignInAlt, 
  FaUserPlus, 
  FaFileAlt,
  FaArrowRight,
  FaExclamationCircle,
  FaKey,
  FaCheckCircle
} from "react-icons/fa";
import "./LoginPage.css";

const LoginPage = () => {
  const { login, debugAuthState } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showTokenSuccess, setShowTokenSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect info from location state
  const redirectTo = location.state?.redirectTo || "/";
  const redirectData = location.state?.redirectData;
  const message = location.state?.message || "";
  const isOrderRedirect = redirectTo === "/order-preview";

  // Pre-fill email if provided in redirect data
  useEffect(() => {
    if (redirectData?.email) {
      setEmail(redirectData.email);
    }
    
    // Check if we have a token warning
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('session') === 'expired') {
      setError("Your session has expired. Please login again.");
    }
  }, [redirectData, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      console.log("🚀 Attempting login...");
      
      // Call login API
      const response = await loginUser({ email, password });
      console.log("✅ Backend login response:", response);
      
      // Extract token from response
      const token = response.token;
      const userData = response.user || response;
      
      if (!token) {
        console.warn("⚠️ No token received from server");
        setError("Login successful but no authentication token received. Some features may not work.");
      } else {
        console.log(`🔑 Token received: ${token.substring(0, 20)}...`);
        setShowTokenSuccess(true);
      }
      
      // Store token and user in context
      login(userData, token);
      
      // Debug current auth state
      debugAuthState();
      
      // Handle redirect after successful login
      if (isOrderRedirect && redirectData) {
        // Navigate to OrderPreviewPage with the saved order data
        navigate("/order-preview", { 
          state: { 
            orderData: redirectData,
            userLoggedIn: true,
            message: "Login successful! Review your order below."
          } 
        });
      } else {
        // Regular dashboard redirect based on role
        const role = userData.role || "CLIENT";
        if (role === "ADMIN") {
          navigate("/admin-dashboard");
        } else if (role === "WRITER") {
          navigate("/writer-dashboard");
        } else {
          navigate("/student-dashboard");
        }
      }
    } catch (err) {
      console.error("❌ Login error:", err);
      setError(err.message || "Login failed. Please check your credentials and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterClick = () => {
    if (isOrderRedirect && redirectData) {
      // Go to register page with order data
      navigate("/register", { 
        state: { 
          message: "Create an account to continue with your order",
          redirectTo: "/order-preview",
          redirectData: redirectData
        } 
      });
    } else {
      navigate("/register");
    }
  };

  const dismissTokenSuccess = () => {
    setShowTokenSuccess(false);
  };

  return (
    <div className="login-page-container">
      <div className="login-background-overlay"></div>
      
      <div className="login-form-container">
        {/* Header */}
        <div className="login-header">
          <div className="login-icon-container">
            <FaSignInAlt className="login-main-icon" />
          </div>
          <h1 className="login-title">Login to Your Account</h1>
          
          {/* Order-specific message */}
          {isOrderRedirect && (
            <div className="login-order-message">
              <FaFileAlt className="login-order-icon" />
              <div className="login-order-text">
                <p><strong>Complete Your Order</strong></p>
                <p>Login to review and confirm your writing order</p>
              </div>
            </div>
          )}
          
          {/* General message */}
          {message && !isOrderRedirect && (
            <div className="login-info-message">
              <FaExclamationCircle className="login-info-icon" />
              <span>{message}</span>
            </div>
          )}
        </div>

        {/* Token Success Modal */}
        {showTokenSuccess && (
          <div className="login-token-success-modal">
            <div className="login-token-success-content">
              <div className="login-token-success-header">
                <FaCheckCircle className="login-token-success-icon" />
                <h3>Secure Login Successful!</h3>
              </div>
              <div className="login-token-success-body">
                <p>Your authentication token has been securely stored.</p>
                <p>This token enables:</p>
                <ul className="login-token-success-list">
                  <li><FaKey /> Secure API communication</li>
                  <li><FaCheckCircle /> Protected payment processing</li>
                  <li><FaFileAlt /> Safe order management</li>
                  <li><FaSignInAlt /> Persistent session</li>
                </ul>
                <p className="login-token-success-note">
                  <small>The token is encrypted and will be automatically included in all your requests.</small>
                </p>
              </div>
              <button 
                onClick={dismissTokenSuccess}
                className="login-token-success-button"
              >
                Continue to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="login-error-alert">
            <FaExclamationCircle className="login-error-icon" />
            <span>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-form-group">
            <label className="login-form-label">
              <FaEnvelope className="login-input-icon" />
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="login-input"
              placeholder="Enter your email address"
              required
              disabled={isLoading}
            />
          </div>

          <div className="login-form-group">
            <label className="login-form-label">
              <FaLock className="login-input-icon" />
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input"
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
          </div>

          <button 
            type="submit" 
            className="login-submit-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="login-spinner"></span>
                Logging in...
              </>
            ) : (
              <>
                <FaSignInAlt className="login-button-icon" />
                {isOrderRedirect ? "Login & Continue Order" : "Login to Account"}
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="login-divider">
          <span>Don't have an account?</span>
        </div>

        {/* Register Link */}
        <button 
          onClick={handleRegisterClick}
          className="login-register-button"
          disabled={isLoading}
        >
          <FaUserPlus className="login-register-icon" />
          {isOrderRedirect ? "Create Account & Continue Order" : "Create New Account"}
          <FaArrowRight className="login-arrow-icon" />
        </button>

        {/* Forgot Password Link */}
        <div className="login-forgot-password">
          <Link to="/forgot-password" className="login-forgot-link">
            Forgot your password?
          </Link>
        </div>

        {/* Security Info */}
        <div className="login-security-info">
          <div className="login-security-icon">
            <FaKey />
          </div>
          <div className="login-security-text">
            <p><strong>Secure Authentication</strong></p>
            <p>Your login uses encrypted tokens for maximum security.</p>
          </div>
        </div>

        {/* Order Preview Info (only for order redirects) */}
        {isOrderRedirect && (
          <div className="login-order-info">
            <div className="login-order-info-content">
              <FaFileAlt className="login-order-info-icon" />
              <div className="login-order-info-text">
                <p><strong>Your order is ready for review</strong></p>
                <p>After logging in, you'll be able to:</p>
                <ul className="login-order-benefits">
                  <li>Review all order details</li>
                  <li>Confirm the price estimate</li>
                  <li>Proceed to secure payment</li>
                  <li>Track your order progress</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;